import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import ServiceLayout from '../../../components/layout/ServiceLayout';
import ServicePageSection from '../../../components/layout/ServicePageSection';
import Container from '../../../components/layout/Container';

// UI components
import { FAQList } from '../../../components/ui/FAQ';
import CompactFeatureCard from '../../../components/ui/CompactFeatureCard';
import ProcessStep from '../../../components/ui/ProcessStep';
import Card from '../../../components/ui/Card';

// Section components
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * AI Onboarding Page - Follows the same formatting as other service pages
 */
const AIOnboardingPage: React.FC = () => {
  // Filter relevant case studies
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.solution === 'Business AI Onboarding' || study.industry === 'Financial Services'
  );

  // Filter relevant FAQs - first 3 items
  const relevantFaqs = faqs.slice(0, 3);

  return (
    <ServiceLayout>
      <Hero
        title="Business AI Onboarding Services"
        subtitle="Seamlessly integrate AI tools into your existing business processes with minimal disruption and maximum impact."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
        backgroundType="gradient"
      />
      
      {/* Overview */}
      <ServicePageSection title="Overview" padding="md">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-neutral-600 leading-relaxed">
            Our Business AI Onboarding services provide a structured, end-to-end approach for integrating AI solutions 
            into your organization. We focus on minimizing disruption while maximizing adoption and ROI, 
            ensuring your transition to AI-enhanced operations is smooth and successful.
          </p>
        </div>
      </ServicePageSection>

      {/* Benefits Section */}
      <ServicePageSection 
        title="The Benefits of Professional AI Onboarding" 
        bgColor="gray" 
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <CompactFeatureCard
            title="Faster Time to Value"
            description="Skip the trial-and-error phase. Our structured approach ensures your AI tools deliver value from day one with properly configured systems and trained users."
            delay={0.1}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <CompactFeatureCard
            title="Minimized Disruption"
            description="Our change management expertise ensures smooth transitions with minimal impact on daily operations and employee productivity."
            delay={0.2}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            }
          />
          <CompactFeatureCard
            title="Higher Adoption Rates"
            description="Comprehensive training and ongoing support lead to confident users who embrace AI tools rather than resist them, maximizing your investment."
            delay={0.3}
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
        </div>
      </ServicePageSection>

      {/* Process Section */}
      <ServicePageSection title="Our AI Onboarding Process" padding="md">
        <div className="grid gap-y-12">
          <ProcessStep 
            number={1} 
            title="Assessment & Planning"
            description="We evaluate your current systems, workflow needs, and team capabilities to create a tailored onboarding plan that aligns with your business objectives."
          />
          <ProcessStep 
            number={2} 
            title="System Integration & Configuration"
            description="Our technical team handles the seamless integration of AI tools with your existing systems, ensuring proper data flows and optimal configuration."
          />
          <ProcessStep 
            number={3} 
            title="Team Training Program"
            description="Customized training sessions prepare your team to effectively use new AI tools, with role-specific guidance and hands-on practice."
          />
          <ProcessStep 
            number={4} 
            title="Guided Implementation"
            description="We work alongside your team during the initial implementation phase, providing real-time support and adjustments as needed."
          />
          <ProcessStep 
            number={5} 
            title="Ongoing Support & Optimization"
            description="Post-implementation support ensures your team remains confident as they integrate AI into daily operations, with regular check-ins to optimize performance."
          />
        </div>
      </ServicePageSection>

      {/* Ideal For Section */}
      <ServicePageSection 
        title="Ideal For Organizations That Need" 
        bgColor="primary"
        padding="md"
      >
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Card className="h-full bg-white/10 border-white/20">
              <h3 className="text-xl font-semibold mb-3">Smooth Transitions to AI-Enabled Workflows</h3>
              <p>
                When implementing new AI tools will significantly change existing processes, our structured 
                onboarding minimizes disruption while maximizing benefits.
              </p>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="h-full bg-white/10 border-white/20">
              <h3 className="text-xl font-semibold mb-3">High User Adoption Rates</h3>
              <p>
                For organizations where employee resistance to new technologies has been a challenge in the past, 
                our comprehensive training approach ensures better acceptance.
              </p>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="h-full bg-white/10 border-white/20">
              <h3 className="text-xl font-semibold mb-3">Technical Expertise Gap-Filling</h3>
              <p>
                When your team lacks the technical expertise to properly configure and implement AI tools, 
                we provide the knowledge transfer needed for long-term success.
              </p>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="h-full bg-white/10 border-white/20">
              <h3 className="text-xl font-semibold mb-3">Accelerated Time-to-Value</h3>
              <p>
                For businesses that need to quickly realize returns on their AI investments, our streamlined 
                onboarding process eliminates common delays and obstacles.
              </p>
            </Card>
          </motion.div>
        </div>
      </ServicePageSection>

      {/* Case Studies Section */}
      {relevantCaseStudies.length > 0 && (
        <ServicePageSection title="Success Stories" padding="md">
          <div className="grid md:grid-cols-2 gap-8">
            {relevantCaseStudies.slice(0, 2).map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={study.url} className="block h-full">
                  <Card hover className="h-full p-6">
                    <div className="mb-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {study.solution}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">{study.title}</h3>
                    <p className="text-neutral-600 text-sm mb-4">{study.excerpt}</p>
                    
                    <div className="space-y-2">
                      {study.results.slice(0, 2).map((result, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-neutral-600">{result}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-neutral-100">
                      <span className="inline-flex items-center text-primary font-medium text-sm">
                        Read full case study
                        <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Link 
              to="/case-studies" 
              className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors"
            >
              View all case studies
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </ServicePageSection>
      )}

      {/* FAQ Section */}
      <ServicePageSection 
        title="Frequently Asked Questions" 
        bgColor="gray"
        padding="md"
      >
        <div className="max-w-3xl mx-auto">
          <FAQList faqs={relevantFaqs} />
          
          <div className="mt-8 text-center">
            <p className="text-neutral-600 mb-4">
              Have more questions about our AI onboarding services?
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors"
            >
              Contact our onboarding specialists
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </ServicePageSection>

      {/* Call to Action */}
      <CallToAction
        title="Ready to transform your business with AI?"
        description="Schedule a consultation to discuss your AI onboarding needs and discover how we can help your organization thrive in the age of AI."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default AIOnboardingPage;